import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import EditProfileDialog from './EditProfileDialog';
import {
  Dashboard as DashboardIcon,
  PhotoLibrary as ImmichIcon,
  Movie as JellyfinIcon,
  Storage as TrueNASIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';

const DEFAULT_DRAWER_WIDTH = 280;
const COLLAPSED_DRAWER_WIDTH = 88;

const StyledDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'collapsed' })(({ theme, collapsed }) => ({
  width: collapsed ? COLLAPSED_DRAWER_WIDTH : DEFAULT_DRAWER_WIDTH,
  flexShrink: 0,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  '& .MuiDrawer-paper': {
    width: collapsed ? COLLAPSED_DRAWER_WIDTH : DEFAULT_DRAWER_WIDTH,
    boxSizing: 'border-box',
    background: theme.palette.background.paper,
    backdropFilter: `blur(${theme.custom?.glassBlur || 24}px)`,
    borderRight: '1px solid rgba(255, 255, 255, 0.1)',
    color: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
  },
}));

const SidebarHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  minHeight: 88,
}));

const LogoImg = styled('img')(({ theme }) => ({
  width: 40,
  height: 40,
  objectFit: 'contain',
  flexShrink: 0,
  filter: 'drop-shadow(0 0 10px rgba(0, 212, 255, 0.3))',
  animation: 'logoPulse 6s ease-in-out infinite',
}));

const NavItem = styled(ListItemButton, { shouldForwardProp: (prop) => prop !== 'active' })(({ theme, active }) => ({
  margin: theme.spacing(0.5, 2),
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  backgroundColor: active ? alpha(theme.palette.primary.main, 0.15) : 'transparent',
  border: active ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}` : '1px solid transparent',
  color: active ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.7)',
  padding: theme.spacing(1, 2),
  '&:hover': {
    backgroundColor: active ? alpha(theme.palette.primary.main, 0.2) : 'rgba(255, 255, 255, 0.05)',
    color: '#ffffff',
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
  },
  '& .MuiListItemIcon-root': {
    color: active ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.5)',
    minWidth: 40,
  },
}));

const UserCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  margin: theme.spacing(2),
  borderRadius: '16px',
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  overflow: 'hidden',
}));

const SidebarIcon = ({ src, alt, active }) => (
  <Box
    component="img"
    src={src}
    alt={alt}
    sx={{
      width: 24,
      height: 24,
      objectFit: 'contain',
      filter: active 
        ? 'drop-shadow(0 0 4px rgba(0, 212, 255, 0.8))' 
        : 'grayscale(20%) opacity(0.8)',
      transition: 'all 0.3s ease',
    }}
  />
);

const Sidebar = ({ 
  activeView, 
  onViewChange, 
  userInfo, 
  onLogout, 
  mobileOpen, 
  onMobileClose,
  collapsed,
  onToggleCollapse,
  isTrueNASOffline,
  sidebarConfig,
  onSidebarConfigChange,
  apiBaseUrl,
  token,
  onUserProfileSave,
  language,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [editProfileOpen, setEditProfileOpen] = useState(false);

  const allowedTabs = userInfo?.allowed_tabs ?? [];
  const isAdmin = userInfo?.is_admin ?? false;

  const navItems = [
    { 
      id: 'dashboard', 
      label: t('sidebar.dashboard'), 
      icon: <SidebarIcon src="/dashboard-icons.png" alt="Dashboard" active={activeView === 'dashboard'} />, 
      visible: true 
    },
    { 
      id: 'oralb', 
      label: t('sidebar.oralb'), 
      icon: <SidebarIcon src="/oralb.png" alt="Oral-B" active={activeView === 'oralb'} />,
      visible: isAdmin || allowedTabs.includes('oralb')
    },
    { 
      id: 'immich', 
      label: t('sidebar.immich'), 
      icon: <SidebarIcon src="/immich.png" alt="Immich" active={activeView === 'immich'} />,
      visible: !isTrueNASOffline && (isAdmin || ['immich_statistics', 'immich_adam_library', 'immich_jobs', 'immich_users', 'immich_libraries', 'immich_nightly_tasks', 'immich_about'].some(tab => allowedTabs.includes(tab)))
    },
    { 
      id: 'jellyfin', 
      label: t('sidebar.jellyfin'), 
      icon: <SidebarIcon src="/jellyfin.png" alt="Jellyfin" active={activeView === 'jellyfin'} />, 
      visible: !isTrueNASOffline && (isAdmin || allowedTabs.includes('jellyfin'))
    },
    { 
      id: 'home_assistant', 
      label: t('sidebar.home_assistant'), 
      icon: <SidebarIcon src="/home-assistant-alt.png" alt="Home Assistant" active={activeView === 'home_assistant'} />,
      visible: isAdmin || ['home_assistant', 'home_assistant_automations', 'home_assistant_room_designer', 'home_assistant_backups'].some(tab => allowedTabs.includes(tab))
    },
    { 
      id: 'polish_tracker', 
      label: t('sidebar.polish_tracker'), 
      icon: <SidebarIcon src="/qbittorrent.png" alt="Polish Tracker" active={activeView === 'polish_tracker'} />, 
      visible: isAdmin || allowedTabs.includes('polish_tracker')
    },
    { 
      id: 'nasdrive', 
      label: t('sidebar.nas_drive'), 
      icon: <SidebarIcon src="/truenas-scale.png" alt="NAS Drive" active={activeView === 'nasdrive'} />,
      visible: !isTrueNASOffline && (isAdmin || allowedTabs.includes('nas_drive'))
    },
    { 
      id: 'unifi', 
      label: t('sidebar.unifi'), 
      icon: <SidebarIcon src="/unifi.png" alt="UniFi" active={activeView === 'unifi'} />,
      visible: isAdmin || allowedTabs.some(tab => tab === 'unifi' || tab.startsWith('unifi_'))
    },
    { 
      id: 'tax', 
      label: t('sidebar.tax'), 
      icon: <SidebarIcon src="/tax.png" alt="Tax" active={activeView === 'tax'} />,
      visible: isAdmin || allowedTabs.includes('tax')
    },
    { 
      id: 'nginx', 
      label: 'Nginx', 
      icon: <SidebarIcon src="/nginx-proxy-manager.png" alt="Nginx Proxy Manager" active={activeView === 'nginx'} />,
      visible: isAdmin || allowedTabs.includes('nginx_proxy_manager')
    },
    { 
      id: 'servers', 
      label: t('sidebar.servers'), 
      icon: <SidebarIcon src="/server.png" alt="Servers" active={activeView === 'servers'} />, 
      visible: isAdmin || ['truenas_services', 'services_truenas', 'services_rpi5', 'debian_sysmon', 'services_ha', 'truenas_cron_jobs', 'truenas_apps', 'truenas_virtual_machines', 'truenas_storage_pools', 'truenas_data_protection', 'task_scheduler'].some(tab => allowedTabs.includes(tab))
    },
    { 
      id: 'settings', 
      label: t('sidebar.settings'), 
      icon: <SettingsIcon sx={{ 
        color: activeView === 'settings' ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.5)',
        filter: activeView === 'settings' ? 'drop-shadow(0 0 4px rgba(0, 212, 255, 0.8))' : 'none'
      }} />,
      visible: isAdmin || ['docker_maintenance', 'docker_security', 'chat', 'game_snake', 'game_server_ping', 'game_disk_whack', 'game_docker_stack', 'nfs_mounts', 'maintenance_changelog', 'maintenance_logs', 'gui_theme', 'logon_page', 'roadmap'].some(tab => allowedTabs.includes(tab))
    },
  ];

  const visibleNavItems = navItems.filter(item => item.visible);
  const configOrder = sidebarConfig?.order && Array.isArray(sidebarConfig.order) ? sidebarConfig.order : null;
  const configGroups = sidebarConfig?.groups && Array.isArray(sidebarConfig.groups) ? sidebarConfig.groups : [];
  const collapsedGroupsSet = new Set(sidebarConfig?.collapsedGroups || []);
  const groupIdsSet = new Set(configGroups.map((g) => g.id));
  const orderIsMixed = configOrder && configOrder.some((id) => groupIdsSet.has(id));

  const itemToGroup = {};
  configGroups.forEach((g) => {
    if (g.label && Array.isArray(g.itemIds)) g.itemIds.forEach((id) => { itemToGroup[id] = g; });
  });

  let renderNodes = [];
  if (orderIsMixed) {
    configOrder.forEach((entryId) => {
      if (groupIdsSet.has(entryId)) {
        const group = configGroups.find((g) => g.id === entryId);
        if (!group || !Array.isArray(group.itemIds)) return;
        const groupItems = (group.itemIds || [])
          .map((id) => navItems.find((n) => n.id === id))
          .filter(Boolean)
          .filter((n) => n.visible);
        if (groupItems.length > 0) renderNodes.push({ type: 'group', group, items: groupItems });
        return;
      }
      if (itemToGroup[entryId]) return;
      const item = navItems.find((n) => n.id === entryId);
      if (item && item.visible) renderNodes.push({ type: 'item', item });
    });
  } else {
    const orderedVisibleItems = configOrder
      ? [
          ...configOrder.filter((id) => visibleNavItems.some((item) => item.id === id)).map((id) => navItems.find((n) => n.id === id)).filter(Boolean),
          ...visibleNavItems.filter((item) => !configOrder.includes(item.id)),
        ]
      : visibleNavItems;
    const groupEmitted = new Set();
    orderedVisibleItems.forEach((item) => {
      const group = itemToGroup[item.id];
      if (!group) {
        renderNodes.push({ type: 'item', item });
        return;
      }
      if (groupEmitted.has(group.id)) return;
      groupEmitted.add(group.id);
      const groupItems = (group.itemIds || [])
        .map((id) => navItems.find((n) => n.id === id))
        .filter(Boolean)
        .filter((n) => n.visible);
      if (groupItems.length === 0) return;
      renderNodes.push({ type: 'group', group, items: groupItems });
    });
  }

  const toggleGroupCollapsed = (groupId) => {
    if (typeof onSidebarConfigChange !== 'function') return;
    const current = sidebarConfig?.collapsedGroups || [];
    const next = current.includes(groupId) ? current.filter((id) => id !== groupId) : [...current, groupId];
    onSidebarConfigChange({ ...sidebarConfig, collapsedGroups: next });
  };

  const drawerContent = (
    <>
      <Box sx={{ overflowX: 'hidden' }}>
        <SidebarHeader sx={{ 
          justifyContent: 'center',
          px: collapsed && !isMobile ? 3 : 3,
        }}>
          <LogoImg src="/favicon_watchtower/WT_TransparentBG.png" alt="WatchTower" />
          {(!collapsed || isMobile) && (
            <Typography 
              variant="h6" 
              sx={{ 
                fontFamily: "'Orbitron', sans-serif",
                fontWeight: 900, 
                fontSize: '1rem',
                letterSpacing: '2px', 
                whiteSpace: 'nowrap',
                color: '#ffffff',
                filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.4))',
              }}
            >
              WatchTower
            </Typography>
          )}
        </SidebarHeader>
        
        <List sx={{ px: 0 }}>
          {renderNodes.map((node, index) => {
            if (node.type === 'item') {
              const item = node.item;
              return (
                <ListItem key={item.id} disablePadding sx={{ display: 'block' }}>
                  <Tooltip title={collapsed && !isMobile ? item.label : ""} placement="right">
                    <NavItem 
                      active={activeView === item.id ? 1 : 0} 
                      onClick={() => {
                        onViewChange(item.id);
                        if (isMobile) onMobileClose();
                      }}
                      sx={{
                        justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                        px: collapsed && !isMobile ? 0 : 2,
                        mx: collapsed && !isMobile ? 2 : 2,
                        '& .MuiListItemIcon-root': {
                          minWidth: collapsed && !isMobile ? 0 : 40,
                          mr: collapsed && !isMobile ? 0 : 0,
                        }
                      }}
                    >
                      <ListItemIcon sx={{ justifyContent: 'center' }}>
                        {item.icon}
                      </ListItemIcon>
                      {(!collapsed || isMobile) && (
                        <ListItemText 
                          primary={item.label} 
                          primaryTypographyProps={{ 
                            fontSize: '0.95rem', 
                            fontWeight: activeView === item.id ? 600 : 500,
                            noWrap: true
                          }} 
                        />
                      )}
                    </NavItem>
                  </Tooltip>
                </ListItem>
              );
            }
            const { group, items } = node;
            const isCollapsed = collapsedGroupsSet.has(group.id);
            const showGroupUi = !collapsed || isMobile;
            if (isCollapsed && showGroupUi) {
              return (
                <ListItem key={`group-${group.id}`} disablePadding sx={{ display: 'block' }}>
                  <NavItem
                    active={0}
                    onClick={() => toggleGroupCollapsed(group.id)}
                    sx={{
                      justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                      px: collapsed && !isMobile ? 0 : 2,
                      mx: collapsed && !isMobile ? 2 : 2,
                      '& .MuiListItemIcon-root': { minWidth: collapsed && !isMobile ? 0 : 40 },
                    }}
                  >
                    {(!collapsed || isMobile) && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <ListItemText 
                          primary={group.label} 
                          primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 600, color: 'rgba(255,255,255,0.6)' }} 
                        />
                        <ChevronRightIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} />
                      </Box>
                    )}
                  </NavItem>
                </ListItem>
              );
            }
            if (isCollapsed) return null;
            return (
              <React.Fragment key={`group-${group.id}`}>
                {showGroupUi && (
                  <Box 
                    sx={{ px: 2, pt: index > 0 ? 1.5 : 0, pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                    onClick={() => toggleGroupCollapsed(group.id)}
                    role="button"
                    aria-label={`Collapse ${group.label}`}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.45)',
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                      }}
                    >
                      {group.label}
                    </Typography>
                    <ExpandMoreIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} />
                  </Box>
                )}
                {items.map((item) => (
                  <ListItem key={item.id} disablePadding sx={{ display: 'block' }}>
                    <Tooltip title={collapsed && !isMobile ? item.label : ""} placement="right">
                      <NavItem 
                        active={activeView === item.id ? 1 : 0} 
                        onClick={() => {
                          onViewChange(item.id);
                          if (isMobile) onMobileClose();
                        }}
                        sx={{
                          justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                          px: collapsed && !isMobile ? 0 : 2,
                          mx: collapsed && !isMobile ? 2 : 2,
                          '& .MuiListItemIcon-root': {
                            minWidth: collapsed && !isMobile ? 0 : 40,
                            mr: collapsed && !isMobile ? 0 : 0,
                          }
                        }}
                      >
                        <ListItemIcon sx={{ justifyContent: 'center' }}>
                          {item.icon}
                        </ListItemIcon>
                        {(!collapsed || isMobile) && (
                          <ListItemText 
                            primary={item.label} 
                            primaryTypographyProps={{ 
                              fontSize: '0.95rem', 
                              fontWeight: activeView === item.id ? 600 : 500,
                              noWrap: true
                            }} 
                          />
                        )}
                      </NavItem>
                    </Tooltip>
                  </ListItem>
                ))}
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      <Box sx={{ overflowX: 'hidden' }}>
        {!isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <IconButton 
              onClick={onToggleCollapse}
              sx={{ 
                color: 'rgba(255, 255, 255, 0.5)',
                background: 'rgba(255, 255, 255, 0.03)',
                '&:hover': { background: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Box>
        )}
        <Divider sx={{ mx: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
        <UserCard sx={{ 
          justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
          px: collapsed && !isMobile ? 0 : 2,
          mx: collapsed && !isMobile ? 2 : 2,
        }}>
          <Tooltip title={collapsed && !isMobile ? (t('sidebar.editProfile') || 'Edit profile') : ''} placement="right">
            <Box
              component="span"
              onClick={() => apiBaseUrl && token && setEditProfileOpen(true)}
              sx={{
                cursor: apiBaseUrl && token ? 'pointer' : 'default',
                display: 'inline-flex',
                '&:hover': apiBaseUrl && token ? { opacity: 0.9 } : {},
              }}
            >
              <Avatar 
                sx={{ 
                  width: 40, 
                  height: 40, 
                  bgcolor: theme.palette.primary.main,
                  fontSize: '1rem',
                  fontWeight: 700,
                  flexShrink: 0,
                  overflow: 'hidden'
                }}
              >
                {userInfo?.avatar_url ? (
                  <img
                    src={userInfo.avatar_url}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  userInfo?.username?.charAt(0).toUpperCase() || 'U'
                )}
              </Avatar>
            </Box>
          </Tooltip>
          {(!collapsed || isMobile) && (
            <>
              <Box 
                sx={{ flexGrow: 1, minWidth: 0, cursor: apiBaseUrl && token ? 'pointer' : 'default' }}
                onClick={() => apiBaseUrl && token && setEditProfileOpen(true)}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#ffffff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {userInfo?.username || 'User'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block' }}>
                  {userInfo?.is_admin ? t('sidebar.administrator') : t('sidebar.user')}
                </Typography>
              </Box>
              <Tooltip title={t('sidebar.logout')}>
                <IconButton 
                  size="small" 
                  onClick={onLogout}
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.5)',
                    '&:hover': { color: theme.palette.error.main, background: alpha(theme.palette.error.main, 0.1) }
                  }}
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </UserCard>
        {collapsed && !isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
             <Tooltip title="Logout" placement="right">
                <IconButton 
                  size="small" 
                  onClick={onLogout}
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.5)',
                    '&:hover': { color: theme.palette.error.main, background: alpha(theme.palette.error.main, 0.1) }
                  }}
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
          </Box>
        )}
      </Box>
      <EditProfileDialog
        open={editProfileOpen}
        onClose={() => setEditProfileOpen(false)}
        apiBaseUrl={apiBaseUrl}
        token={token}
        userInfo={userInfo}
        language={language}
        sidebarConfig={sidebarConfig}
        onSave={onUserProfileSave}
      />
      <style>{`
        @keyframes logoPulse {
          0%, 100% { filter: drop-shadow(0 0 8px rgba(0, 212, 255, 0.3)); transform: scale(1); }
          50% { filter: drop-shadow(0 0 15px rgba(0, 212, 255, 0.6)); transform: scale(1.05); }
        }
      `}</style>
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: DEFAULT_DRAWER_WIDTH,
            background: (theme) => theme.palette.background.paper,
            backdropFilter: (theme) => `blur(${theme.custom?.glassBlur || 24}px)`,
            borderRight: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#ffffff',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <StyledDrawer variant="permanent" open collapsed={collapsed}>
      {drawerContent}
    </StyledDrawer>
  );
};

export default Sidebar;
